import { useEffect } from 'react';

import { usePageView } from '@crehana/analytics';
import { AuthTalentProvider } from '@crehana/auth';
import { UIThemeProvider } from '@crehana/compass.ui';
import { Toaster } from '@crehana/compass.ui/toaster';
import { PageLoader } from '@crehana/compass.ui/v2';
import { FeatureFlagProvider } from '@crehana/feature-flags/react';
import type { TFetchFlagApiVersion } from '@crehana/feature-flags/store';
import { PageNotFound } from '@crehana/next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

// NOW THE INTERCOM SETUP LIVES IN @crehana/compass.layouts/components/load-intercom.universal
// DELETE THIS ONCE THE LAYOUTS INTERCOM COMPONENT HAS BEEN SUCCESSFULLY DEPLOYED TO PRODUCTION
// import IntercomChat from '@/views/AdminDashboard/components/IntercomChat';

import { FEATURE_FLAGS_V5_KEYS } from './featureFlags';
import usePermissions from './hooks/usePermissions';

const AdmindashboardView: React.FC<
  React.PropsWithChildren<{ slug: string }>
> = ({ children, slug }) => {
  const router = useRouter();
  const { showView, loading, error } = usePermissions({ slug });
  const { sendPageView } = usePageView({
    type: 'page',
    pageData: {
      data: {
        name: 'B2B Admin',
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (router.pathname) {
      sendPageView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen bg-base-lighter-8">
        <PageLoader size="s" />
      </div>
    );
  }

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (error || !showView) return <PageNotFound />;

  return (
    <>
      <AuthTalentProvider>{children}</AuthTalentProvider>
    </>
  );
};

const AdmindashboardViewWithAuthProvider: React.FC<
  React.PropsWithChildren<{ slug: string; organizationId: number }>
> = ({ children, organizationId, slug }) => {
  const featureFlagsV5 = FEATURE_FLAGS_V5_KEYS.map(
    flag => [flag, 'V5'] as [string, TFetchFlagApiVersion],
  );

  return (
    <>
      <NextSeo
        title="Panel Administrativo"
        titleTemplate="%s | Crehana"
        noindex
        nofollow
      />
      <UIThemeProvider>
        <FeatureFlagProvider
          flags={[...featureFlagsV5]}
          organizationId={organizationId}
        >
          <AdmindashboardView slug={slug}>{children}</AdmindashboardView>
        </FeatureFlagProvider>

        {/* NOW THE INTERCOM SETUP LIVES IN @crehana/compass.layouts/components/load-intercom.universal
        DELETE THIS ONCE THE LAYOUTS INTERCOM COMPONENT HAS BEEN SUCCESSFULLY DEPLOYED TO PRODUCTION */}
        {/* <IntercomChat /> */}
        <Toaster />
      </UIThemeProvider>
    </>
  );
};

export default AdmindashboardViewWithAuthProvider;
